<template>
    <Form v-if="isEditing" @submit="onSubmit">
        <div class="grid grid-cols-3 col-span-3 space-y-2 items-center mb-2">
            <TextInput
                id="customer-name"
                label="Name"
                placeholder="Customer Name"
                :value="name"
                :errors="nameError"
                :isEditing="isEditing"
                :required="true"
                @update:value="(value: string) => (name = value)"
            />
            <TextInput
                id="customer-email"
                label="Email"
                placeholder="joe@bloggs.com"
                type="email"
                :value="email"
                :errors="emailError"
                :isEditing="isEditing"
                :required="true"
                @update:value="(value: string) => (email = value)"
            />
            <TextInput
                id="customer-telephone"
                label="Telephone"
                placeholder="01487 523654"
                type="tel"
                :value="telephone"
                :errors="telephoneError"
                :isEditing="isEditing"
                @update:value="(value: string) => (telephone = value)"
            />

            <TextInput
                id="customer-mobile"
                label="Mobile"
                placeholder="07541236665"
                type="tel"
                :value="mobile"
                :errors="mobileError"
                :isEditing="isEditing"
                @update:value="(value: string) => (mobile = value)"
            />
        </div>

        <AddressLookup
            :hasTopPadding="true"
            id="address-lookup"
            :errors="errors"
            :show-manual-entry-button="false"
            :address="address"
            :general-error-text="undefined"
            @update:value="v => (updateAddress(v))"
        />


    </Form>
    <div v-else class="grid gap-y-2 dark:text-gray-100">
        <div class="grid grid-cols-4">
            <div class="grid-cols-1"><strong>Name :</strong></div>
            <div class="grid-cols-3">{{ name }}</div>
        </div>

        <div class="grid grid-cols-4">
            <div class="grid-cols-1"><strong>Email :</strong></div>
            <div class="grid-cols-3">{{ email }}</div>
        </div>

        <div class="grid grid-cols-4">
            <div class="grid-cols-1"><strong>Telephone :</strong></div>
            <div class="grid-cols-3">{{ telephone }}</div>
        </div>

        <div class="grid grid-cols-4">
            <div class="grid-cols-1"><strong>Mobile :</strong></div>
            <div class="grid-cols-3">{{ mobile }}</div>
        </div>

        <div class="grid grid-cols-4">
            <div class="grid-cols-1"><strong>Address :</strong></div>
            <div class="grid-cols-3 whitespace-pre">
                {{ addressToString }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, inject, onErrorCaptured, PropType, ref, watch} from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { Customer } from "../types/Customer";
import useMiscInformationStore from "../../../utils/useMiscInformationStore";
import AddressLookup from "../../../components/forms/AddressLookup.vue";

export default defineComponent({
    components: {AddressLookup},
    props: {
        customer: {
            type: Object as PropType<Customer>,
            required: false
        },
        isEditing: {
            type: Boolean,
            required: true
        }
    },
    setup(props, context) {
        const miscInformationStore = useMiscInformationStore();
        const isEditing = computed(() => props.isEditing);

        // Form Validation Schema
        const schema = yup.object({
            name: yup.string().required('Name is required.').max(255),
            email: yup.string().required('Email is required.').email('Email needs to be a valid email.').max(255),
            telephone: yup.string().nullable().transform((o, c) => o === "" ? null : c).min(6, 'Telephone needs to be a valid number.').max(255),
            mobile:  yup.string().nullable().transform((o, c) => o === "" ? null : c).min(6, 'Mobile needs to be a valid number.').max(255),
            address_line_1: yup.string().nullable().transform((o, c) => o === "" ? null : c).min(5, 'Address line 1 needs to be a minimum of 5 characters.').max(255),
            address_line_2: yup.string().nullable().max(255),
            address_line_3: yup.string().nullable().max(255),
            address: yup.string().nullable().max(255),
            city: yup.string().nullable().max(255),
            county: yup.string().nullable().max(255),
            country_id: yup.string().required('Country is required.'),
            zip_code: yup.string().nullable().max(255)
        });
        // Form context
        const {handleSubmit, errors, resetForm} = useForm({
            validationSchema: schema,
        })

        // form fields
        // No need to define rules for fields
        const { value: name, errorMessage: nameError } = useField('name');
        const { value: email, errorMessage: emailError } = useField('email');
        const { value: telephone, errorMessage: telephoneError } = useField('telephone');
        const { value: mobile, errorMessage: mobileError } = useField('mobile');
        const { value: addressLine1, errorMessage: addressLine1Error } = useField('address_line_1');
        const { value: addressLine2, errorMessage: addressLine2Error } = useField('address_line_2');
        const { value: addressLine3, errorMessage: addressLine3Error } = useField('address_line_3');
        const { value: city, errorMessage: cityError } = useField('city');
        const { value: county, errorMessage: countyError } = useField('county');
        const { value: countryId, errorMessage: countryIdError } = useField('country_id');
        const { value: zipCode, errorMessage: zipCodeError } = useField('zip_code');


        if (props.customer) {
            name.value = props.customer.name ?? "";
            email.value = props.customer.email ?? "";
            telephone.value = props.customer.telephone ?? "";
            mobile.value = props.customer.mobile ?? "";
            addressLine1.value = props.customer.address_line_1 ?? "";
            addressLine2.value = props.customer.address_line_2 ?? "";
            addressLine3.value = props.customer.address_line_3 ?? "";
            city.value = props.customer.city ?? "";
            county.value = props.customer.county ?? "";
            countryId.value = props.customer.country_id ?? "GBR";
            zipCode.value = props.customer.zip_code ?? "";
        } else {
            countryId.value = "GBR";
        }
        const address =  computed( () => {
            return {
                addressLine1: addressLine1.value,
                addressLine2: addressLine2.value,
                addressLine3: addressLine3.value,
                city: city.value,
                county: county.value,
                countryId: countryId.value ?? "GBR",
                zipCode: zipCode.value
            };
        });

        const updateAddress = function(value: any) {
            addressLine1.value = value.address_line_1;
            addressLine2.value = value.address_line_2;
            addressLine3.value = value.address_line_3;
            city.value = value.city;
            county.value = value.county;
            zipCode.value = value.zip_code;
            countryId.value = value.country_id;
        }

        const addressToString = computed(() => {
            return [
                addressLine1.value,
                addressLine2.value,
                addressLine3.value,
                city.value,
                county.value,
                countryId.value,
                zipCode.value
            ].filter(e=>e).join(", "+'\n')
        })

        const onSubmit = handleSubmit(values => {
            context.emit('onSubmit', values);
        });


        watch(
            isEditing,
            () => {
                resetForm();

                name.value = props.customer?.name;
                email.value = props.customer?.email;
                telephone.value = props.customer?.telephone;
                addressLine1.value = props.customer?.address_line_1;
                addressLine2.value = props.customer?.address_line_2;
                addressLine3.value = props.customer?.address_line_3;
                city.value = props.customer?.city;
                countryId.value = props.customer?.country_id;
                zipCode.value = props.customer?.zip_code;

                if (props.customer?.mobile) {
                    mobile.value = props.customer?.mobile;
                }

                if (props.customer?.address_line_2) {
                    addressLine2.value = props.customer?.address_line_2;
                }

                if (props.customer?.address_line_3) {
                    addressLine3.value = props.customer?.address_line_3;
                }

                if (props.customer?.county) {
                    county.value = props.customer?.county;
                }
            },
            { immediate: true }
        );

        return {
            countries: miscInformationStore.countries,
            isEditing,
            onSubmit,

            address,

            name,
            nameError,

            email,
            emailError,

            telephone,
            telephoneError,

            mobile,
            mobileError,

            addressLine1,
            addressLine1Error,

            addressLine2,
            addressLine2Error,

            addressLine3,
            addressLine3Error,

            city,
            cityError,

            county,
            countyError,

            countryId,
            countryIdError,

            zipCode,
            zipCodeError,

            addressToString,
            updateAddress,
            errors
        }
    },
})
</script>
